import {
  Badge,
  Box,
  Button,
  Column,
  EmptyState,
  Menu,
  MenuActionsButton,
  MenuList,
  MenuItem,
  PlusIcon,
  Row,
  SparkleIcon,
  Text,
  Tooltip,
  WarningIcon,
  useDisclosure,
  useToast,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { formatDistanceToNowStrict } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "react-query";

import genericPlaceholder from "src/assets/placeholders/generic.svg";
import { Card } from "src/components/card";
import { DeleteButton } from "src/components/delete-button";
import { PageSpinner } from "src/components/loading";
import {
  AIDRole,
  useAIDPermissions,
} from "src/components/permission/use-aid-permissions";
import { JobStatus } from "@hightouch/lib/message-queue/job-status";
import {
  type DecisionEngineFeatureMatricesForEngineQuery,
  type GetAgenticUfmJobsQuery,
  useCreateDecisionEngineFeatureMatrixMutation,
  useCreateDecisionEngineFeatureMatrixRelationshipMutation,
  useDecisionEngineFeatureMatricesForEngineQuery,
  useDeleteDecisionEngineFeatureMatrixMutation,
  useGetAgenticUfmJobsQuery,
  useMinimalDecisionEngineFlowsQuery,
} from "src/graphql";
import { Table } from "src/ui/table";
import { Link } from "src/router";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { ParentIcon } from "src/pages/schema/icons";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { useNavigate } from "src/router";
import { TextWithTooltip } from "src/components/text-with-tooltip";

import { GenerateAgenticUfmDialog } from "./generate-agentic-ufm-dialog";

type FeatureMatrixRow =
  DecisionEngineFeatureMatricesForEngineQuery["decision_engine_feature_matrices"][number];

function countFeatures(fm: FeatureMatrixRow): number {
  return fm.relationships.reduce((total, rel) => {
    const config = rel.config as {
      columns?: { name: string; aggregations?: string[] }[];
    } | null;
    return total + (config?.columns?.length ?? 0);
  }, 0);
}

export function FeatureMatrices() {
  const { engine } = useDecisionEngineContext();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isPermitted } = useAIDPermissions({
    minRequiredRole: AIDRole.AidAdmin,
  });
  const { aidAiUfm } = useFlags();
  const {
    isOpen: generateOpen,
    onOpen: onGenerateOpen,
    onClose: onGenerateClose,
  } = useDisclosure();

  const queryKey = useDecisionEngineFeatureMatricesForEngineQuery.getKey({
    decisionEngineId: engine.id,
  });

  const { data: featureMatrices, isLoading } =
    useDecisionEngineFeatureMatricesForEngineQuery(
      { decisionEngineId: engine.id },
      {
        select: (data) => data.decision_engine_feature_matrices,
      },
    );

  const flowsQuery = useMinimalDecisionEngineFlowsQuery(
    { id: engine.id },
    { select: (data) => data.decision_engine_flows },
  );
  const flowParentModelIds = useMemo(
    () =>
      new Set(
        (flowsQuery.data ?? [])
          .map((flow) => flow.parent_model?.id)
          .filter((id): id is string => Boolean(id))
          .map(String),
      ),
    [flowsQuery.data],
  );

  const deleteMutation = useDeleteDecisionEngineFeatureMatrixMutation();
  const createFmMutation = useCreateDecisionEngineFeatureMatrixMutation();
  const createRelMutation =
    useCreateDecisionEngineFeatureMatrixRelationshipMutation();

  const { refetchInterval: governedRefetchInterval } =
    useGovernedRefetchInterval<GetAgenticUfmJobsQuery["getAgenticUFMJobs"]>({
      compareKey: (data) => data?.map((job) => [job.jobId, job.status]),
    });

  const { data: ufmJobs } = useGetAgenticUfmJobsQuery(
    { decisionEngineId: engine.id },
    {
      enabled: aidAiUfm,
      select: (data) => data.getAgenticUFMJobs,
      refetchInterval: (data, query) =>
        data?.some(
          (job) =>
            job.status === JobStatus.Queued ||
            job.status === JobStatus.Processing,
        )
          ? governedRefetchInterval(data, query)
          : false,
    },
  );

  // Refresh the tile list when a generation run we saw as active completes.
  const activeUfmJobIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!ufmJobs) return;
    const completed = ufmJobs.some(
      (job) =>
        activeUfmJobIds.current.has(job.jobId) &&
        job.status === JobStatus.Success,
    );
    activeUfmJobIds.current = new Set(
      ufmJobs
        .filter(
          (job) =>
            job.status === JobStatus.Queued ||
            job.status === JobStatus.Processing,
        )
        .map((job) => job.jobId),
    );
    if (completed) {
      queryClient.invalidateQueries(queryKey);
    }
  }, [ufmJobs, queryClient, queryKey]);

  if (isLoading) {
    return <PageSpinner />;
  }

  const handleDuplicate = async (fm: FeatureMatrixRow) => {
    try {
      const existingNames = new Set(featureMatrices?.map((f) => f.name) ?? []);
      let candidateName = `${fm.name} (copy)`;
      let counter = 2;
      while (existingNames.has(candidateName)) {
        candidateName = `${fm.name} (copy ${counter})`;
        counter++;
      }

      const result = await createFmMutation.mutateAsync({
        object: {
          name: candidateName,
          parent_model_id: fm.parent_model?.id,
          decision_engine_id: engine.id,
        },
      });
      const newId = result.insert_decision_engine_feature_matrices_one?.id;
      if (newId) {
        await Promise.all(
          fm.relationships.map((rel) =>
            createRelMutation.mutateAsync({
              object: {
                decision_engine_feature_matrix_id: newId,
                relationship_id: rel.relationship_id,
                config: rel.config,
                enabled: rel.enabled,
              },
            }),
          ),
        );
      }
      await queryClient.invalidateQueries(queryKey);
      toast({
        id: "duplicate-feature-matrix",
        title: `Feature matrix "${fm.name}" duplicated`,
        variant: "success",
      });
    } catch {
      toast({
        id: "duplicate-feature-matrix",
        title: "Failed to duplicate feature matrix",
        variant: "error",
      });
    }
  };

  if (!featureMatrices?.length) {
    return (
      <>
        <EmptyState
          m={6}
          imageUrl={genericPlaceholder}
          title="No feature matrices"
          message="Create a feature matrix to define which columns and aggregations to use for your agents."
          actions={
            <Row gap={2}>
              <Button
                variant="primary"
                isDisabled={!isPermitted}
                onClick={() =>
                  navigate("/ai/configuration/feature-matrices/new")
                }
              >
                New feature matrix
              </Button>
              {aidAiUfm && (
                <Button
                  icon={SparkleIcon}
                  isDisabled={!isPermitted}
                  onClick={onGenerateOpen}
                >
                  Generate with AI
                </Button>
              )}
            </Row>
          }
        />
        <Column px={6}>
          <AgenticUfmJobsTable jobs={ufmJobs} />
        </Column>
        {aidAiUfm && (
          <GenerateAgenticUfmDialog
            isOpen={generateOpen}
            onClose={onGenerateClose}
          />
        )}
      </>
    );
  }

  return (
    <Column gap={4} overflow="auto">
      <Row gap={4} flexWrap="wrap">
        {featureMatrices.map((fm) => (
          <FeatureMatrixTile
            key={fm.id}
            fm={fm}
            isPermitted={isPermitted}
            flowParentModelIds={flowParentModelIds}
            onClick={() =>
              navigate(`/ai/configuration/feature-matrices/${fm.id}`)
            }
            onDelete={async () => {
              await deleteMutation.mutateAsync({ id: fm.id });
              await queryClient.invalidateQueries(queryKey);
            }}
            onDuplicate={() => handleDuplicate(fm)}
          />
        ))}
        <Card
          className="group"
          href="/ai/configuration/feature-matrices/new"
          shadow="buttonShadowLight"
          borderRadius="2xl"
          border="1px dashed"
          borderColor="base.divider"
          _hover={{
            boxShadow: "sm",
            borderColor: "primary.border",
            background: "primary.background",
          }}
          width="300px"
          minHeight="142px"
        >
          <Row
            align="center"
            justify="center"
            height="100%"
            color="text.secondary"
            _groupHover={{ color: "primary.base" }}
          >
            <Text fontWeight="medium" size="lg" color="inherit">
              <Box as={PlusIcon} boxSize={6} mr={2} />
              New feature matrix
            </Text>
          </Row>
        </Card>
        {aidAiUfm && isPermitted && (
          <Card
            className="group"
            onClick={onGenerateOpen}
            shadow="buttonShadowLight"
            borderRadius="2xl"
            border="1px dashed"
            borderColor="base.divider"
            _hover={{
              boxShadow: "sm",
              borderColor: "primary.border",
              background: "primary.background",
            }}
            width="300px"
            minHeight="142px"
          >
            <Row
              align="center"
              justify="center"
              height="100%"
              color="text.secondary"
              _groupHover={{ color: "primary.base" }}
            >
              <Text fontWeight="medium" size="lg" color="inherit">
                <Box as={SparkleIcon} boxSize={6} mr={2} />
                Generate with AI
              </Text>
            </Row>
          </Card>
        )}
      </Row>
      <AgenticUfmJobsTable jobs={ufmJobs} />
      {aidAiUfm && (
        <GenerateAgenticUfmDialog
          isOpen={generateOpen}
          onClose={onGenerateClose}
        />
      )}
    </Column>
  );
}

const UFM_JOB_STATUS_BADGES: Record<
  string,
  { label: string; variant: "info" | "success" | "error" | "subtle" }
> = {
  [JobStatus.Queued]: { label: "Queued", variant: "subtle" },
  [JobStatus.Processing]: { label: "Running", variant: "info" },
  [JobStatus.Success]: { label: "Completed", variant: "success" },
  [JobStatus.Failed]: { label: "Failed", variant: "error" },
  [JobStatus.Cancelling]: { label: "Canceling", variant: "subtle" },
  [JobStatus.Cancelled]: { label: "Canceled", variant: "subtle" },
};

function AgenticUfmJobsTable({
  jobs,
}: {
  jobs: GetAgenticUfmJobsQuery["getAgenticUFMJobs"] | undefined;
}) {
  if (!jobs?.length) {
    return null;
  }

  return (
    <Column gap={2} maxWidth="4xl">
      <Text fontWeight="medium">Recent AI generations</Text>
      <Table
        primaryKey="jobId"
        data={jobs}
        columns={[
          {
            name: "Agent",
            cell: ({ agentName }) => (
              <TextWithTooltip>{agentName ?? "Unknown"}</TextWithTooltip>
            ),
          },
          {
            name: "Status",
            cell: ({ status }) => {
              const badge = UFM_JOB_STATUS_BADGES[status];
              return badge ? (
                <Badge variant={badge.variant}>{badge.label}</Badge>
              ) : (
                <Badge variant="subtle">{status}</Badge>
              );
            },
          },
          {
            name: "Started",
            cell: ({ createdAt }) => (
              <Text color="text.secondary">
                {formatDistanceToNowStrict(new Date(createdAt), {
                  addSuffix: true,
                })}
              </Text>
            ),
          },
          {
            name: "Details",
            cell: ({ status, error, featureMatrixId }) =>
              status === JobStatus.Failed && error ? (
                <TextWithTooltip color="text.danger">{error}</TextWithTooltip>
              ) : featureMatrixId ? (
                <Link
                  href={`/ai/configuration/feature-matrices/${featureMatrixId}`}
                >
                  View
                </Link>
              ) : null,
          },
        ]}
      />
    </Column>
  );
}

function FeatureMatrixTile({
  fm,
  isPermitted,
  flowParentModelIds,
  onClick,
  onDelete,
  onDuplicate,
}: {
  fm: FeatureMatrixRow;
  isPermitted: boolean;
  flowParentModelIds: Set<string>;
  onClick: () => void;
  onDelete: () => Promise<unknown>;
  onDuplicate: () => void;
}) {
  const featureCount = countFeatures(fm);
  const { flows, parent_model, name, updated_at } = fm;
  const agentCount = flows.length;
  const isModelMismatch =
    parent_model &&
    flowParentModelIds.size > 0 &&
    !flowParentModelIds.has(String(parent_model.id));

  return (
    <Card
      onClick={onClick}
      width="300px"
      p={5}
      _hover={{ borderColor: "primary.base" }}
    >
      <Column gap={3}>
        <Row justify="space-between" align="center" position="relative">
          <Row gap={2} align="center" flex={1} minWidth={0}>
            <TextWithTooltip fontWeight="medium">{name}</TextWithTooltip>
            {isModelMismatch && (
              <Tooltip message="No agent in this decision engine uses this feature matrix's parent model, so it can't be assigned to one.">
                <Box
                  as={WarningIcon}
                  boxSize={4}
                  flexShrink={0}
                  color="warning.base"
                />
              </Tooltip>
            )}
          </Row>
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            position="absolute"
            right={0}
          >
            <Menu isLazy={false}>
              <MenuActionsButton />
              <MenuList>
                <MenuItem isDisabled={!isPermitted} onClick={onDuplicate}>
                  Duplicate
                </MenuItem>
                <DeleteButton
                  variant="menu"
                  label="feature matrix"
                  isDisabled={!isPermitted}
                  onDelete={onDelete}
                />
              </MenuList>
            </Menu>
          </Box>
        </Row>

        {parent_model && (
          <Row gap={2}>
            <ParentIcon size="sm" />
            <TextWithTooltip size="sm">{parent_model.name}</TextWithTooltip>
          </Row>
        )}

        <Column gap={1}>
          <Text color="text.secondary" size="sm">
            {pluralize("feature", featureCount, true)}
            {" · "}
            <Tooltip
              placement="auto"
              isDisabled={agentCount === 0}
              message={
                <Column>
                  {flows.map((f) => (
                    <Text key={f.id} color="inherit">
                      {f.name}
                    </Text>
                  ))}
                </Column>
              }
            >
              {`Used by ${pluralize("agent", agentCount, true)}`}
            </Tooltip>
          </Text>
        </Column>

        <Text color="text.tertiary" size="sm">
          Last updated{" "}
          {formatDistanceToNowStrict(new Date(updated_at), {
            addSuffix: true,
          })}
        </Text>
      </Column>
    </Card>
  );
}
